import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import { DataTable } from "../../components/data-table";
import {
  BUSINESS_TRANSACTION_STATUSES,
  BUSINESS_TRANSACTION_TYPES,
  type BusinessTransactionStatusFilter,
  type BusinessTransactionType,
} from "../../lib/api/adminBusinessTransactions";
import { useAdminBusinessTransactionsQuery } from "../../lib/api/useAdminBusinessTransactionsQuery";
import MerchantTransactionDetailModal from "./MerchantTransactionDetailModal";
import {
  MERCHANT_TABLE_LIMIT,
  buildMerchantTransactionRows,
  createMerchantTransactionColumns,
  type MerchantTransactionRow,
} from "./merchantTransactionsTable";

function errorMessage(
  err: { response?: { data?: unknown } } | null | undefined,
  fallback: string
): string {
  const data = err?.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    return String((data as { message?: string }).message);
  }
  return fallback;
}

type Props = {
  /** Scopes every request to one merchant; omit for the portfolio-wide view. */
  businessId?: string;
  from?: string;
  to?: string;
  enabled?: boolean;
  /** Title used by the export modal and file name. */
  exportTitle: string;
  heading?: string;
};

const MerchantTransactionsPanel: React.FC<Props> = ({
  businessId,
  from,
  to,
  enabled = true,
  exportTitle,
  heading = "Merchant transactions",
}) => {
  const [page, setPage] = useState(1);
  const [txType, setTxType] = useState<BusinessTransactionType>("deposits");
  const [status, setStatus] = useState<BusinessTransactionStatusFilter>("all");
  const [exportOpen, setExportOpen] = useState(false);
  const [detailTx, setDetailTx] = useState<MerchantTransactionRow["raw"] | null>(
    null
  );

  useEffect(() => {
    setPage(1);
  }, [businessId, from, to, txType, status]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminBusinessTransactionsQuery({
      page,
      type: txType,
      status,
      businessId,
      from,
      to,
      limit: MERCHANT_TABLE_LIMIT,
      enabled,
    });

  const rows = useMemo(
    () => buildMerchantTransactionRows(data?.transactions ?? [], txType),
    [data?.transactions, txType]
  );

  const openDetail = useCallback((row: MerchantTransactionRow) => {
    setDetailTx(row.raw);
  }, []);

  const columns = useMemo(
    () => createMerchantTransactionColumns(txType, openDetail),
    [txType, openDetail]
  );

  const meta = data?.meta;

  return (
    <>
      <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
        <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
          Activity
        </h4>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
            aria-label="Export merchant summary and transactions"
          >
            <BiExport className="text-white text-lg" aria-hidden />
            <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
              Export
            </span>
          </button>
        </div>
      </div>

      <div className="mt-2 mb-10">
        <h3 className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest mb-3">
          {heading}
        </h3>

        <div
          className="mb-3 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Transaction type"
        >
          {BUSINESS_TRANSACTION_TYPES.map((opt) => {
            const active = txType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTxType(opt.value)}
                className={
                  active
                    ? "rounded-full border border-secondary_button bg-secondary_button/15 px-4 py-2 text-[12px] font-bold text-secondary_button"
                    : "rounded-full border border-[#2C363E] bg-[#161E25]/80 px-4 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="mb-4">
          <label htmlFor="merchants-status-filter" className="sr-only">
            Status filter
          </label>
          <select
            id="merchants-status-filter"
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as BusinessTransactionStatusFilter)
            }
            className="h-10 rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
          >
            {BUSINESS_TRANSACTION_STATUSES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isFetching && !isPending && (
          <p className="text-[12px] text-[#869AA9] mb-2">Updating list…</p>
        )}

        {isError && (
          <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>
              {errorMessage(error, "Could not load merchant transactions.")}
            </span>
            <button
              type="button"
              onClick={() => refetch()}
              className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
            >
              Retry
            </button>
          </div>
        )}

        {isPending && !isError && (
          <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
            <TailSpin color="#0B75C2" height={36} width={36} />
          </div>
        )}

        {!isPending && !isError && (
          <DataTable<MerchantTransactionRow>
            columns={columns}
            data={rows}
            currentPage={page}
            setCurrentPage={setPage}
            totalPage={Math.max(1, meta?.totalPages ?? 1)}
            count={meta?.totalTransactions ?? 0}
            limit={MERCHANT_TABLE_LIMIT}
            emptyContent={`No ${txType} for this range.`}
            className="rounded-xl border border-[#2C363E] overflow-hidden"
            fixedBodyHeight="min(560px, 52vh)"
          />
        )}
      </div>

      {detailTx && (
        <MerchantTransactionDetailModal
          transaction={detailTx}
          onClose={() => setDetailTx(null)}
        />
      )}

      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={exportTitle}
        exportContext={{
          type: "businesses",
          businessTxType: txType,
          businessStatus: status,
          businessId,
        }}
      />
    </>
  );
};

export default MerchantTransactionsPanel;
